// iot-admin/client/src/components/HexMap.js
// HexMap V2 — Interactive Hex Playground
// Light map with visible places, custom tooltip, smooth interactions
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { cellToBoundary } from 'h3-js';
import api, { cityService } from '../services/api';
import {
  Layers, Crosshair, X, Tag, History, Radio
} from 'lucide-react';

// ────────────────────────────────────────────────────────────────
// Color helpers — continuous HSL gradients, solid hex colors
// ────────────────────────────────────────────────────────────────
const ROAD_COLORS = {
  highway:     { fill: '#dc2626', stroke: '#991b1b', label: 'Highway' },
  primary:     { fill: '#ea580c', stroke: '#9a3412', label: 'Primary' },
  secondary:   { fill: '#ca8a04', stroke: '#854d0e', label: 'Secondary' },
  tertiary:    { fill: '#16a34a', stroke: '#166534', label: 'Tertiary' },
  residential: { fill: '#2563eb', stroke: '#1e40af', label: 'Residential' },
  service:     { fill: '#7c3aed', stroke: '#5b21b6', label: 'Service' },
  default:     { fill: '#94a3b8', stroke: '#64748b', label: 'Other' },
};

function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360; s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => { const k = (n + h / 30) % 12; return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)))); };
  return '#' + [f(0), f(8), f(4)].map(v => v.toString(16).padStart(2, '0')).join('');
}
function lerp(a, b, t) { return a + (b - a) * Math.max(0, Math.min(1, t)); }

function getSignalColor(signal) {
  const NONE = { fill: '#94a3b8', stroke: '#64748b', opacity: 0.35 };
  if (!signal || !signal.value) return NONE;
  const conf = signal.confidence || 1.0;
  const opacity = lerp(0.4, 0.85, conf);
  const type = signal.type;

  if (type === 'weather') {
    const temp = signal.value.temp_c;
    if (temp == null) return NONE;
    let hue;
    if (temp <= 0) hue = 240;
    else if (temp <= 20) hue = lerp(240, 120, temp / 20);
    else if (temp <= 35) hue = lerp(120, 40, (temp - 20) / 15);
    else hue = lerp(40, 0, Math.min((temp - 35) / 15, 1));
    return { fill: hslToHex(hue, 80, 48), stroke: hslToHex(hue, 80, 32), opacity };
  }
  if (type === 'traffic_density') {
    const score = signal.value.density_score;
    if (score == null) return NONE;
    const hue = lerp(120, 0, score);
    return { fill: hslToHex(hue, 85, 48), stroke: hslToHex(hue, 85, 30), opacity };
  }
  if (type === 'aqi') {
    const aqi = signal.value.aqi_value;
    if (aqi == null) return NONE;
    let hue;
    if (aqi <= 150) hue = lerp(120, 40, aqi / 150);
    else hue = lerp(40, 0, Math.min((aqi - 150) / 150, 1));
    return { fill: hslToHex(hue, 80, 48), stroke: hslToHex(hue, 80, 32), opacity };
  }
  if (type === 'scene_context') {
    const poiCount = signal.value.poi_count || 0;
    let hue;
    if (poiCount <= 0) hue = 270;
    else if (poiCount <= 5) hue = lerp(270, 120, poiCount / 5);
    else if (poiCount <= 15) hue = lerp(120, 40, (poiCount - 5) / 10);
    else hue = lerp(40, 0, Math.min((poiCount - 15) / 10, 1));
    return { fill: hslToHex(hue, 70, 48), stroke: hslToHex(hue, 70, 32), opacity };
  }
  return NONE;
}
function getRoadColor(hex) {
  if (!hex.road) return ROAD_COLORS.default;
  return ROAD_COLORS[hex.roadType] || ROAD_COLORS.default;
}

// Signal display meta
const SIGNAL_META = {
  weather:         { emoji: '🌤', label: 'Weather',     color: '#f59e0b' },
  traffic_density: { emoji: '🚗', label: 'Traffic',     color: '#ef4444' },
  aqi:             { emoji: '💨', label: 'Air Quality', color: '#8b5cf6' },
  scene_context:   { emoji: '📍', label: 'Scene',       color: '#06b6d4' },
};

// ────────────────────────────────────────────────────────────────
// HexTooltip — floats near mouse, shows hex summary
// ────────────────────────────────────────────────────────────────
const HexTooltip = ({ hex, mousePos, colorBy }) => {
  if (!hex) return null;
  return (
    <div className="hextt" style={{ left: mousePos.x + 16, top: mousePos.y - 12 }}>
      {/* Location */}
      {hex.signal?.value?.neighborhood && (
        <div className="hextt-title">📍 {hex.signal.value.neighborhood}</div>
      )}
      {hex.road && hex.roadName && (
        <div className="hextt-road">🛣️ {hex.roadName}</div>
      )}

      {/* Signal data */}
      {hex.signal && hex.signal.value && (
        <div className="hextt-sig">
          {hex.signal.type === 'weather' && (
            <div className="hextt-row">
              <span className="hextt-icon">🌤</span>
              <div>
                <div className="hextt-val">{hex.signal.value.temp_c}°C</div>
                <div className="hextt-sub">
                  {hex.signal.value.humidity}% RH{hex.signal.value.conditions ? ` · ${hex.signal.value.conditions}` : ''}
                </div>
              </div>
            </div>
          )}
          {hex.signal.type === 'traffic_density' && (
            <div className="hextt-row">
              <span className="hextt-icon">🚗</span>
              <div>
                <div className="hextt-val">
                  {((hex.signal.value.density_score || 0) * 100).toFixed(0)}%
                  <span className="hextt-unit">density</span>
                </div>
                <div className="hextt-sub">
                  {hex.signal.value.congestion_level?.replace('_', ' ') || ''}
                  {hex.signal.value.speed_kmh ? ` · ${hex.signal.value.speed_kmh} km/h` : ''}
                </div>
              </div>
            </div>
          )}
          {hex.signal.type === 'aqi' && (
            <div className="hextt-row">
              <span className="hextt-icon">💨</span>
              <div>
                <div className="hextt-val">AQI {hex.signal.value.aqi_value}</div>
                <div className="hextt-sub">{hex.signal.value.category || ''}</div>
              </div>
            </div>
          )}
          {hex.signal.type === 'scene_context' && (
            <div className="hextt-row">
              <span className="hextt-icon">🏙</span>
              <div>
                <div className="hextt-val">{hex.signal.value.poi_count || 0} POIs</div>
                <div className="hextt-sub">{hex.signal.value.sublocality || ''}</div>
              </div>
            </div>
          )}
          {hex.signal.inherited && (
            <div className="hextt-inherited">↑ Inherited from res-{hex.signal.inherited}</div>
          )}
        </div>
      )}

      {/* Road mode extra */}
      {colorBy === 'road' && hex.road && (
        <div className="hextt-row" style={{ marginTop: 2 }}>
          <span className="hextt-swatch" style={{ background: (ROAD_COLORS[hex.roadType] || ROAD_COLORS.default).fill }} />
          <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{hex.roadType || 'road'}</span>
          {hex.roadDist != null && <span className="hextt-sub" style={{ marginLeft: 4 }}>{hex.roadDist.toFixed(0)}m</span>}
        </div>
      )}

      <div className="hextt-h3">{hex.h3}</div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// HexInspector — slide-out detail panel (click a hex)
// ────────────────────────────────────────────────────────────────
const HexInspector = ({ cityId, h3Index, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [history, setHistory] = useState(null);
  const [historySignal, setHistorySignal] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHours, setHistoryHours] = useState(24);

  useEffect(() => {
    if (!h3Index) return;
    setLoading(true); setDetail(null); setHistory(null); setHistorySignal(null); setActiveTab('overview');
    cityService.getHexDetail(cityId, h3Index)
      .then(res => setDetail(res.data))
      .catch(err => console.error('Hex detail error:', err))
      .finally(() => setLoading(false));
  }, [cityId, h3Index]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const fetchHistory = useCallback(async (sig, hours = 24) => {
    setHistoryLoading(true);
    try {
      const res = await cityService.getHexHistory(cityId, h3Index, { signal: sig, hours, limit: 200 });
      setHistory(res.data); setHistorySignal(sig); setHistoryHours(hours); setActiveTab('history');
    } catch (err) { console.error('Hex history error:', err); }
    finally { setHistoryLoading(false); }
  }, [cityId, h3Index]);

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';
  const fmtConf = (c) => c != null ? `${(c * 100).toFixed(0)}%` : '—';
  if (!h3Index) return null;

  const tabs = [
    { key: 'overview', icon: <Crosshair size={13} />, label: 'Cell' },
    { key: 'signals',  icon: <Radio size={13} />,     label: 'Signals' },
    { key: 'labels',   icon: <Tag size={13} />,       label: 'Labels' },
    { key: 'history',  icon: <History size={13} />,    label: 'History' },
  ];

  return (
    <div className="hex-inspector">
      {/* Header */}
      <div className="hex-insp-header">
        <div className="hex-insp-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`hex-insp-tab ${activeTab === t.key ? 'active' : ''}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <button onClick={onClose} className="hex-insp-close"><X size={16} /></button>
      </div>

      <div className="hex-insp-h3"><code>{h3Index}</code></div>

      {loading ? (
        <div className="hex-insp-loading">Loading...</div>
      ) : !detail ? (
        <div className="hex-insp-error">Failed to load</div>
      ) : (
        <div className="hex-insp-body">
          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="hex-insp-section">
              <div className="hex-kv"><span>Resolution</span><strong>res-{detail.cell.resolution}</strong></div>
              <div className="hex-kv"><span>Center</span><span className="hex-mono">{detail.cell.lat?.toFixed(5)}, {detail.cell.lng?.toFixed(5)}</span></div>
              <div className="hex-kv">
                <span>Road</span>
                {detail.cell.isRoad
                  ? <span className="hex-badge success">{detail.cell.roadClassification || 'Yes'}</span>
                  : <span className="hex-badge muted">No</span>}
              </div>
              {detail.cell.roadName && <div className="hex-kv"><span>Road Name</span><strong>{detail.cell.roadName}</strong></div>}
              {detail.cell.roadDistanceM != null && <div className="hex-kv"><span>Distance</span><span>{detail.cell.roadDistanceM.toFixed(1)}m</span></div>}
              <div className="hex-kv">
                <span>Device</span>
                {detail.cell.deviceActive ? <span className="hex-badge success">Active</span> : <span className="hex-badge muted">None</span>}
              </div>
              {detail.cell.childCount > 0 && <div className="hex-kv"><span>Children</span><span>{detail.cell.childCount}</span></div>}

              <div className="hex-signal-grid">
                {['weather', 'traffic_density', 'aqi', 'scene_context'].map(sig => {
                  const s = detail.signals[sig];
                  const meta = SIGNAL_META[sig];
                  let val = '—';
                  if (s) {
                    if (sig === 'weather') val = `${s.value?.temp_c || '?'}°C`;
                    if (sig === 'traffic_density') val = `${((s.value?.density_score || 0) * 100).toFixed(0)}%`;
                    if (sig === 'aqi') val = s.value?.aqi_value || '?';
                    if (sig === 'scene_context') val = `${s.value?.poi_count || 0} POIs`;
                  }
                  return (
                    <div key={sig} className="hex-signal-mini" style={{ borderLeftColor: s ? meta.color : '#e2e8f0' }}>
                      <div className="hex-signal-mini-label">{meta.emoji} {meta.label}</div>
                      <div className="hex-signal-mini-val">{val}</div>
                      {s && <div className="hex-signal-mini-conf">conf: {fmtConf(s.confidence)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SIGNALS */}
          {activeTab === 'signals' && (
            <div>
              {['weather', 'traffic_density', 'aqi', 'scene_context'].map(sig => {
                const s = detail.signals[sig];
                const meta = SIGNAL_META[sig];
                return (
                  <div key={sig} className="hex-signal-card">
                    <div className="hex-signal-card-head">
                      <span style={{ fontSize: '1rem' }}>{meta.emoji}</span>
                      <span style={{ fontWeight: 600, flex: 1 }}>{meta.label}</span>
                      {s && (
                        <button onClick={() => fetchHistory(sig, historyHours)} className="hex-hist-btn" disabled={historyLoading}>
                          <History size={12} />
                        </button>
                      )}
                    </div>
                    {s ? (
                      <>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                          {s.inherited
                            ? <span className="hex-badge warning">↑ res-{s.inherited}</span>
                            : <span className="hex-badge success">native</span>}
                          <span className="hex-signal-conf">conf: {fmtConf(s.confidence)}</span>
                        </div>
                        <pre className="hex-signal-json">{JSON.stringify(s.value, null, 1)}</pre>
                        <div className="hex-signal-ts">{fmtDate(s.ts)} · via {s.source || '?'}</div>
                      </>
                    ) : <div className="hex-signal-empty">No data</div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* LABELS */}
          {activeTab === 'labels' && (
            <div>
              {detail.labels?.length > 0 ? (
                <>
                  <div className="hex-label-count">{detail.labels.length} labels</div>
                  <div className="hex-label-grid">
                    {detail.labels.map((l, i) => (
                      <div key={i} className="hex-label-chip">
                        <span className="hex-label-type">{l.type}</span>
                        <span className="hex-label-value">{typeof l.value === 'object' ? JSON.stringify(l.value) : l.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div className="hex-insp-empty">No labels derived yet</div>}
            </div>
          )}

          {/* HISTORY */}
          {activeTab === 'history' && (
            <div>
              {!historySignal ? (
                <div className="hex-insp-empty">Select a signal to view history</div>
              ) : (
                <>
                  <div className="hex-hist-controls">
                    <span className="hex-hist-title">{historySignal.replace(/_/g, ' ')}</span>
                    {[1, 6, 24, 72, 168].map(h => (
                      <button key={h} onClick={() => fetchHistory(historySignal, h)}
                        className={`hex-hour-btn ${historyHours === h ? 'active' : ''}`}>
                        {h < 24 ? `${h}h` : `${h / 24}d`}
                      </button>
                    ))}
                  </div>
                  {historyLoading ? (
                    <div className="hex-insp-loading">Loading...</div>
                  ) : history ? (
                    <div className="hex-hist-scroll">
                      <table className="hex-hist-table">
                        <thead><tr><th>Time</th><th>Values</th><th>Conf</th></tr></thead>
                        <tbody>
                          {history.snapshots.map((snap, i) => (
                            <tr key={i}>
                              <td>{new Date(snap.ts).toLocaleTimeString()}</td>
                              <td><SnapSummary type={historySignal} value={snap.value} /></td>
                              <td>{fmtConf(snap.confidence)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="hex-hist-footer">
                        {history.count} snapshots{history.truncated ? ' (truncated)' : ''}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SnapSummary = ({ type, value }) => {
  if (!value) return <span className="hex-muted">—</span>;
  if (type === 'weather') return <span>{value.temp_c}°C {value.humidity}% {value.conditions || ''}</span>;
  if (type === 'traffic_density') return <span>d:{value.density_score?.toFixed(2)} {value.congestion_level || ''}</span>;
  if (type === 'aqi') return <span>AQI:{value.aqi_value} {value.category || ''}</span>;
  if (type === 'scene_context') return <span>{value.neighborhood || ''} POI:{value.poi_count || 0}</span>;
  return <span>{JSON.stringify(value).slice(0, 60)}</span>;
};

// ────────────────────────────────────────────────────────────────
// HexMap — Main map component (V2)
// ────────────────────────────────────────────────────────────────
const HexMap = ({ cityId, city }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polygonsRef = useRef([]);
  const debounceRef = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [resolution, setResolution] = useState(9);
  const [colorBy, setColorBy] = useState('road');
  const [signal, setSignal] = useState('traffic_density');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedHex, setSelectedHex] = useState(null);
  const [hexCount, setHexCount] = useState(0);

  // Tooltip state
  const [hoveredHex, setHoveredHex] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Track mouse position on the map container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    el.addEventListener('mousemove', handler);
    return () => el.removeEventListener('mousemove', handler);
  }, []);

  // Load Google Maps API
  useEffect(() => {
    if (window.google && window.google.maps) { setMapLoaded(true); return; }
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const interval = setInterval(() => {
        if (window.google && window.google.maps) { setMapLoaded(true); clearInterval(interval); }
      }, 100);
      return () => clearInterval(interval);
    }
    const load = async () => {
      try {
        const { data: cfg } = await api.get('/maps/config');
        if (!cfg.success || !cfg.mapsUrl) return;
        const script = document.createElement('script');
        script.src = cfg.mapsUrl;
        script.async = true;
        script.defer = true;
        script.onload = () => setTimeout(() => setMapLoaded(true), 100);
        document.head.appendChild(script);
      } catch (err) { console.error('Failed to load Google Maps:', err); }
    };
    load();
  }, []);

  // Initialize map — LIGHT CLEAN STYLE
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;
    const center = city?.bbox
      ? { lat: (city.bbox.south + city.bbox.north) / 2, lng: (city.bbox.west + city.bbox.east) / 2 }
      : { lat: 12.9716, lng: 77.5946 };

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: resolution === 8 ? 11 : resolution === 9 ? 13 : 15,
      mapTypeId: 'roadmap',
      styles: cleanMapStyle,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      fullscreenControlOptions: { position: window.google.maps.ControlPosition.RIGHT_BOTTOM },
      zoomControl: true,
      zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
      gestureHandling: 'greedy',
    });

    mapInstanceRef.current.addListener('idle', () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchHexes(), 400);
    });

    mapInstanceRef.current.addListener('click', () => setSelectedHex(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded]);

  // Resolution / color mode change
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const zoom = resolution === 8 ? 11 : resolution === 9 ? 13 : 15;
    const currentZoom = mapInstanceRef.current.getZoom();
    if (Math.abs(currentZoom - zoom) > 2) mapInstanceRef.current.setZoom(zoom);
    fetchHexes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution, colorBy, signal]);

  const fetchHexes = useCallback(async () => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    setLoading(true); setError(null);
    try {
      const res = await cityService.getHexMap(cityId, {
        resolution, south: sw.lat(), west: sw.lng(), north: ne.lat(), east: ne.lng(),
        colorBy, signal: colorBy === 'signal' ? signal : undefined,
      });
      if (res.success) {
        setHexCount(res.data.count);
        drawPolygons(res.data.hexes);
        if (res.data.truncated) setError('Showing first 25k hexes');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityId, resolution, colorBy, signal]);

  const drawPolygons = useCallback((hexes) => {
    for (const p of polygonsRef.current) p.setMap(null);
    polygonsRef.current = [];
    if (!hexes || hexes.length === 0) return;
    const map = mapInstanceRef.current;

    for (const hex of hexes) {
      try {
        const boundary = cellToBoundary(hex.h3);
        const paths = boundary.map(([lat, lng]) => ({ lat, lng }));
        const color = colorBy === 'signal' ? getSignalColor(hex.signal) : getRoadColor(hex);

        let baseFillOpacity;
        if (colorBy === 'signal') {
          baseFillOpacity = color.opacity != null ? color.opacity : 0.55;
          if (hex.signal?.inherited) baseFillOpacity *= 0.55;
        } else {
          baseFillOpacity = 0.55;
        }

        const polygon = new window.google.maps.Polygon({
          paths,
          strokeColor: color.stroke,
          strokeOpacity: 0.6,
          strokeWeight: 0.8,
          fillColor: color.fill,
          fillOpacity: baseFillOpacity,
          map,
          zIndex: hex.road ? 2 : 1,
        });

        polygon._baseFillOpacity = baseFillOpacity;
        polygon._baseZIndex = hex.road ? 2 : 1;

        polygon.addListener('click', (e) => {
          if (e.stop) e.stop();
          setSelectedHex(hex.h3);
        });

        polygon.addListener('mouseover', () => {
          polygon.setOptions({
            strokeWeight: 2.5,
            strokeOpacity: 1,
            fillOpacity: Math.min(0.92, baseFillOpacity + 0.3),
            zIndex: 20,
          });
          setHoveredHex(hex);
        });

        polygon.addListener('mouseout', () => {
          polygon.setOptions({
            strokeWeight: 0.8,
            strokeOpacity: 0.6,
            fillOpacity: polygon._baseFillOpacity,
            zIndex: polygon._baseZIndex,
          });
          setHoveredHex(null);
        });

        polygonsRef.current.push(polygon);
      } catch (e) { /* skip invalid h3 */ }
    }
  }, [colorBy]);

  const signalOptions = [
    { value: 'weather', label: '🌤 Weather' },
    { value: 'traffic_density', label: '🚗 Traffic' },
    { value: 'aqi', label: '💨 AQI' },
    { value: 'scene_context', label: '📍 Scene' },
  ];

  return (
    <div ref={containerRef} className="hexmap-v2">
      {/* Map canvas — shrinks when inspector open */}
      <div ref={mapRef} className={`hexmap-canvas ${selectedHex ? 'with-inspector' : ''}`}>
        {!mapLoaded && (
          <div className="hexmap-loading">
            <div className="hexmap-spinner" />
            <span>Loading map…</span>
          </div>
        )}
      </div>

      {/* ── Floating Toolbar ── */}
      <div className="hexmap-toolbar">
        <div className="hexmap-tool-group">
          <Layers size={14} className="hexmap-tool-icon" />
          {[8, 9, 10].map(r => (
            <button key={r} onClick={() => setResolution(r)}
              className={`hexmap-tool-btn ${resolution === r ? 'active' : ''}`}>
              R{r}
            </button>
          ))}
        </div>

        <div className="hexmap-tool-divider" />

        <div className="hexmap-tool-group">
          <button onClick={() => setColorBy('road')}
            className={`hexmap-tool-btn ${colorBy === 'road' ? 'active' : ''}`}>
            🛣 Road
          </button>
          <button onClick={() => setColorBy('signal')}
            className={`hexmap-tool-btn ${colorBy === 'signal' ? 'active' : ''}`}>
            📡 Signal
          </button>
          {colorBy === 'signal' && (
            <select value={signal} onChange={e => setSignal(e.target.value)} className="hexmap-select">
              {signalOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
        </div>

        <div className="hexmap-tool-divider" />

        <div className="hexmap-tool-group">
          {loading && <span className="hexmap-loading-dot" />}
          <span className="hexmap-hex-count">{hexCount.toLocaleString()} hexes</span>
          {error && <span className={error.startsWith('Showing') ? 'hexmap-hint' : 'hexmap-error'}>{error}</span>}
        </div>
      </div>

      {/* ── Floating Legend ── */}
      <div className="hexmap-legend">
        {colorBy === 'road' ? (
          Object.values(ROAD_COLORS).map(c => (
            <span key={c.label} className="hexmap-legend-item">
              <span className="hexmap-legend-swatch" style={{ background: c.fill }} />
              {c.label}
            </span>
          ))
        ) : (
          <>
            <span className="hexmap-legend-item">
              <span className="hexmap-legend-swatch" style={{ background: '#94a3b8' }} /> None
            </span>
            <div className="hexmap-legend-gradient" />
            <span className="hexmap-legend-range">Low → High</span>
            <span className="hexmap-legend-note">opacity = confidence</span>
          </>
        )}
      </div>

      {/* ── Tooltip (follows mouse) ── */}
      <HexTooltip hex={hoveredHex} mousePos={mousePos} colorBy={colorBy} />

      {/* ── Inspector (slide-in from right) ── */}
      {selectedHex && (
        <HexInspector cityId={cityId} h3Index={selectedHex} onClose={() => setSelectedHex(null)} />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Clean, light map style — roads, places, neighborhoods visible
// ────────────────────────────────────────────────────────────────
const cleanMapStyle = [
  { featureType: 'all', elementType: 'geometry', stylers: [{ saturation: -25 }, { lightness: 8 }] },
  { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#c9daf2' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#5b7fa6' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#f2f2f2' }] },
  { featureType: 'poi.park', elementType: 'geometry.fill', stylers: [{ color: '#d4edda' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'poi.school', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'poi.medical', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#fce4b3' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#e8c88f' }] },
  { featureType: 'road.arterial', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.local', elementType: 'geometry.fill', stylers: [{ color: '#fafafa' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#555555' }] },
  { featureType: 'administrative.neighborhood', elementType: 'labels.text.fill', stylers: [{ color: '#333333' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#222222' }] },
  { featureType: 'transit.station', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'transit.line', stylers: [{ visibility: 'simplified' }] },
];

export default HexMap;
